/**
 * "Save this for whom?" — destination picker for a manual memory save (web).
 *
 * Mirrors the mobile `MemorySubjectPickerSheet`. Shown only when the thread
 * makes the destination genuinely ambiguous (a thread about someone else, or
 * a compatibility pair). Self/general threads keep the existing one-tap save.
 *
 * Why ask at all: saving always to `self` misattributes notes made in another
 * person's thread, and always saving to the thread subject just reverses the
 * error — people frequently say something about themselves inside someone
 * else's thread. Only the user knows which they meant.
 *
 * Selecting a row never writes; the caller attaches
 * `target_selection_version: 1` from its Save handler alone.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  /** Permitted subject ids, `self` first. */
  targets: string[];
  /** Canonical id → label map. Never derive a label from an id here. */
  labels: Record<string, string>;
  /** Row to preselect, or null when the thread makes none unambiguous. */
  defaultTarget: string | null;
  saving?: boolean;
  onSave: (subjectId: string) => void;
  onCancel: () => void;
};

const MemorySubjectPickerDialog: React.FC<Props> = ({
  open,
  targets,
  labels,
  defaultTarget,
  saving,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation('chat');
  // `null` is a real state: an ambiguous compatibility thread must require one
  // deliberate choice before Save becomes available.
  const [selected, setSelected] = useState<string | null>(defaultTarget);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Restore focus to whatever opened the dialog, so keyboard users are not
  // dropped at the top of the document on cancel.
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    setSelected(defaultTarget);
    // Move focus into the dialog for screen readers and Escape handling.
    requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open, defaultTarget]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      // Outside-click cancels. Writes nothing, matching the sheet on mobile.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('memory_target.title', { defaultValue: 'Save this for whom?' })}
        tabIndex={-1}
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl outline-none dark:bg-gray-800"
      >
        <p className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {t('memory_target.eyebrow', { defaultValue: 'MEMORY' })}
        </p>
        <h2 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('memory_target.title', { defaultValue: 'Save this for whom?' })}
        </h2>

        <div className="mt-4 space-y-2" role="radiogroup">
          {targets.map((id) => {
            const active = id === selected;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelected(id)}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                  active
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                    : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'
                }`}
              >
                <span className="truncate text-gray-900 dark:text-gray-100">
                  {id === 'self'
                    ? t('memory_target.you', { defaultValue: 'You' })
                    : labels[id] || t('memory_target.other', { defaultValue: 'Other chart' })}
                </span>
                {active ? <span className="ml-3 text-amber-600">✓</span> : null}
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('memory_target.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            // Disabled until a row is chosen, and while a save is in flight so
            // a double submit cannot write the item twice.
            disabled={!selected || !!saving}
            onClick={() => selected && onSave(selected)}
            className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {t('memory_target.save', { defaultValue: 'Save' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemorySubjectPickerDialog;
